import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserData } from "../features/auth/authSlice";
import { setDialog } from "../features/dialog/dialogSlice";
import { startMemberTour } from "../features/tour/tourSlice";
import { useGetTenantPlanQuery } from "../features/tenants/tenantsApiSlice";
import {
  hasFeatureAccess,
  normalizePlanId,
  FEATURE_KEYS,
  PLAN_IDS,
} from "../utils/planFeatures";
import useAuth from "../hooks/useAuth";

const TILES = [
  {
    key: "meine-daten",
    title: "Meine Daten",
    description: "Persönliche Angaben und Vertrag einsehen",
    icon: "mdi:account-circle-outline",
    color: "#1976d2",
    path: "/dashboard/meine-daten",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    key: "standorte",
    title: "Standorte",
    description: "Alle Standortinfos und Detailansicht auf einen Blick",
    icon: "fa-solid:torii-gate",
    color: "#00695c",
    path: "/dashboard/standorte",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    key: "testing-fees",
    title: "Prüfungsgebühren",
    description: "Offene und bezahlte Gebühren im Blick",
    icon: "mdi:cash-check",
    color: "#2e7d32",
    path: "/dashboard/testing-fees",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    key: "news",
    title: "Neuigkeiten",
    description: "Aktuelle Nachrichten aus dem Verein",
    icon: "mdi:newspaper-variant-outline",
    color: "#7b1fa2",
    path: "/dashboard/news",
    featureKey: FEATURE_KEYS.NEWS,
  },
  {
    key: "e-dojang",
    title: "E-Dojang",
    description: "Online-Lernen mit Lehrvideos und Prüfungsprogramm",
    icon: "fa-solid:yin-yang",
    color: "#8d6e63",
    path: "/dashboard/e-dojang",
    featureKey: FEATURE_KEYS.E_DOJANG,
  },
  {
    key: "tasks",
    title: "Aufgaben",
    description: "Ihre offenen Aufgaben und To-dos",
    icon: "majesticons:analytics-plus",
    color: "#ed6c02",
    path: "/dashboard/tasks",
    featureKey: FEATURE_KEYS.TASKS,
  },
  {
    key: "veranstaltungen",
    title: "Veranstaltungen",
    description: "Termine und Veranstaltungen Ihres Vereins",
    icon: "fa-solid:calendar-check",
    color: "#0288d1",
    path: "/dashboard/veranstaltungen",
    featureKey: FEATURE_KEYS.EVENTS,
  },
  {
    key: "shop",
    title: "Shop",
    description: "Produkte und Bestellungen",
    icon: "game-icons:shop",
    color: "#c62828",
    path: "/dashboard/shop",
    featureKey: FEATURE_KEYS.SHOP,
  },
  {
    key: "survey",
    title: "Befragungen",
    description: "An Umfragen teilnehmen",
    icon: "wpf:survey",
    color: "#00838f",
    path: "/dashboard/survey",
    featureKey: FEATURE_KEYS.SURVEY,
  },
  {
    key: "support",
    title: "Hilfe & Support",
    description: "Bei Fragen oder Problemen",
    icon: "mdi:lifebuoy",
    color: "#f57c00",
    path: "/dashboard/support",
    featureKey: null,
  },
  {
    key: "profil",
    title: "Mein Profil",
    description: "Benutzerkonto und Einstellungen",
    icon: "mdi:account-cog-outline",
    color: "#5c6bc0",
    path: "/dashboard/profil",
    featureKey: null,
  },
];

export default function MemberHomePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasShownWelcomeRef = useRef(false);
  const userData = useSelector(selectCurrentUserData);
  const { isMember, isUser, isEditor, isAdmin, isSuperAdmin } = useAuth();
  const hasStaffRole = isUser || isEditor || isAdmin || isSuperAdmin;
  const isMemberOnly = isMember && !hasStaffRole;

  const { data: tenant } = useGetTenantPlanQuery();
  const planId = normalizePlanId(tenant?.billingPlan || PLAN_IDS.BASIS);

  useEffect(() => {
    if (hasShownWelcomeRef.current) return;
    if (userData?.initialLogin && isMemberOnly) {
      dispatch(
        setDialog({
          title: "Willkommen!",
          type: "CREATE",
          content: "member-welcome",
          data: userData,
        }),
      );
      hasShownWelcomeRef.current = true;
    }
  }, [dispatch, isMemberOnly, userData]);

  const visibleTiles = TILES.filter(
    (tile) => !tile.featureKey || hasFeatureAccess(planId, tile.featureKey),
  );

  const hasPlanTiles = visibleTiles.some((t) => t.featureKey);

  const handleStartTour = () => {
    dispatch(startMemberTour());
  };

  const greeting = (() => {
    const title = (userData?.title || "").trim();
    const firstName = (userData?.firstName || "").trim();
    const lastName = (userData?.lastName || "").trim();
    if (title && lastName) return `Willkommen, ${title} ${lastName}!`;
    if (firstName) return `Hallo, ${firstName}!`;
    return "Willkommen!";
  })();

  return (
    <>
      <Helmet>
        <title>MA | Startseite</title>
      </Helmet>

      <Container maxWidth="sm" sx={{ pb: 4 }}>
        <Box data-tour="member-greeting" sx={{ mb: 3, mt: 1 }}>
          <Typography variant="h4" gutterBottom>
            {greeting}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Was möchten Sie heute tun?
          </Typography>
        </Box>

        {!hasPlanTiles && (
          <Card variant="outlined" sx={{ p: 3, mb: 2, textAlign: "center" }}>
            <Icon
              icon="mdi:information-outline"
              width={40}
              style={{ color: theme.palette.info.main, marginBottom: 8 }}
            />
            <Typography variant="body1" color="text.secondary">
              Ihr Verein hat den Mitgliederbereich noch nicht freigeschaltet.
              Bitte wenden Sie sich an Ihren Vereinsadministrator.
            </Typography>
          </Card>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {visibleTiles.map((tile) => (
            <Card
              key={tile.key}
              data-tour={`member-${tile.key}`}
              variant="outlined"
              sx={{
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  transform: "translateY(-1px)",
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(tile.path)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1.5,
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: `${tile.color}14`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mr: 2,
                  }}
                >
                  <Icon
                    icon={tile.icon}
                    width={28}
                    style={{ color: tile.color }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {tile.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap={false}
                  >
                    {tile.description}
                  </Typography>
                </Box>

                <Icon
                  icon="mdi:chevron-right"
                  width={24}
                  style={{ color: theme.palette.text.disabled, flexShrink: 0 }}
                />
              </CardActionArea>
            </Card>
          ))}
        </Box>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Button
            variant="text"
            color="primary"
            startIcon={<Icon icon="mdi:compass-outline" />}
            onClick={handleStartTour}
          >
            Tour nochmals starten
          </Button>
        </Box>
      </Container>
    </>
  );
}
